import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Folder, Plus, ArrowRight, Search, LayoutGrid, FileText, Users,
  Clock, ChevronRight, Sparkles, ArrowLeft, FolderOpen, MoveRight,
  Trash2
} from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { toast } from 'sonner';
import { authFetch } from '../lib/api';

const API = process.env.REACT_APP_BACKEND_URL;

const CHANNEL_ICONS = {
  'Paid Search': '🔍', 'Paid Social': '📱', 'Programmatic': '📊',
  'SEO': '🌐', 'Influencer': '⭐',
};

const ProjectsView = ({ user }) => {
  const [projects, setProjects] = useState([]);
  const [driveClients, setDriveClients] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);

  // Create project dialog
  const [showDialog, setShowDialog] = useState(false);
  const [dialogStep, setDialogStep] = useState('template');
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [newProject, setNewProject] = useState({ name: '', client_id: '', drive_folder_id: '', monday_board_id: '', template_id: '' });
  const [users, setUsers] = useState([]);
  const [mondayBoards, setMondayBoards] = useState([]);
  const [mondayConfigured, setMondayConfigured] = useState(false);

  // Move project dialog
  const [moveProject, setMoveProject] = useState(null);
  const [moveTargetFolder, setMoveTargetFolder] = useState('');

  // Delete confirmation
  const [deleteProject, setDeleteProject] = useState(null);

  const isAdmin = user.role === 'Admin' || user.role === 'Media_Buyer';

  const fetchData = useCallback(async () => {
    try {
      const [projRes, driveRes] = await Promise.all([
        authFetch(`${API}/api/projects`),
        authFetch(`${API}/api/drive/clients`),
      ]);
      if (projRes.ok) {
        const d = await projRes.json();
        setProjects(d.projects || []);
      }
      if (driveRes.ok) {
        const d = await driveRes.json();
        setDriveClients(d.clients || []);
      }
    } catch (e) { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    if (isAdmin) {
      authFetch(`${API}/api/project-templates`).then(async (r) => {
        if (r.ok) { const d = await r.json(); setTemplates(d.templates || []); }
      });
      authFetch(`${API}/api/users`).then(async (r) => {
        if (r.ok) { const d = await r.json(); setUsers(d.users?.filter(u => u.role === 'Client') || []); }
      });
      authFetch(`${API}/api/monday/boards`).then(async (r) => {
        if (r.ok) { const d = await r.json(); setMondayBoards(d.boards || []); setMondayConfigured(d.configured !== false); }
      });
    }
  }, [isAdmin]);

  // Derived data
  const folderProjectCounts = {};
  projects.forEach(p => {
    const fid = p.drive_folder_id || '_unassigned';
    folderProjectCounts[fid] = (folderProjectCounts[fid] || 0) + 1;
  });

  const folderProjects = useMemo(() => selectedFolder
    ? projects.filter(p => p.drive_folder_id === selectedFolder.drive_folder_id)
    : [], [selectedFolder, projects]);

  const filteredFolderProjects = useMemo(() => searchQuery
    ? folderProjects.filter(p => p.name.toLowerCase().includes(searchQuery.toLowerCase()))
    : folderProjects, [searchQuery, folderProjects]);

  const filteredFolders = useMemo(() => searchQuery && !selectedFolder
    ? driveClients.filter(c => c.folder_name.toLowerCase().includes(searchQuery.toLowerCase()))
    : driveClients, [searchQuery, selectedFolder, driveClients]);

  const unassignedProjects = useMemo(() => projects.filter(p => !p.drive_folder_id), [projects]);

  // Handlers
  const handleCreateProject = async (e) => {
    e.preventDefault();
    const payload = { ...newProject };
    if (selectedFolder && !payload.drive_folder_id) {
      payload.drive_folder_id = selectedFolder.drive_folder_id;
    }
    if (!payload.drive_folder_id) {
      toast.error('Please select a Drive folder');
      return;
    }
    try {
      const res = await authFetch(`${API}/api/projects`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        toast.success('Project created!');
        setShowDialog(false);
        resetCreateDialog();
        fetchData();
      } else {
        const d = await res.json();
        toast.error(d.detail || 'Failed to create project');
      }
    } catch { toast.error('Network error'); }
  };

  const handleMoveProject = async () => {
    if (!moveProject || !moveTargetFolder) return;
    try {
      const res = await authFetch(`${API}/api/projects/${moveProject.id}/folder`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ drive_folder_id: moveTargetFolder }),
      });
      if (res.ok) {
        toast.success(`Moved "${moveProject.name}"`);
        setMoveProject(null);
        setMoveTargetFolder('');
        fetchData();
      } else {
        const d = await res.json();
        toast.error(d.detail || 'Failed to move project');
      }
    } catch { toast.error('Network error'); }
  };

  const handleDeleteProject = async () => {
    if (!deleteProject) return;
    try {
      const res = await authFetch(`${API}/api/projects/${deleteProject.id}`, { method: 'DELETE' });
      if (res.ok) {
        toast.success(`Deleted "${deleteProject.name}"`);
        setDeleteProject(null);
        fetchData();
      } else {
        const d = await res.json();
        toast.error(d.detail || 'Failed to delete');
      }
    } catch { toast.error('Network error'); }
  };

  const resetCreateDialog = () => {
    setDialogStep('template');
    setSelectedTemplate(null);
    setNewProject({ name: '', client_id: '', drive_folder_id: selectedFolder?.drive_folder_id || '', monday_board_id: '', template_id: '' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand"></div>
      </div>
    );
  }

  // ── FOLDER DETAIL VIEW ──
  if (selectedFolder) {
    return (
      <div className="space-y-6" data-testid="folder-detail-view">
        {/* Back + Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost" size="sm"
              onClick={() => { setSelectedFolder(null); setSearchQuery(''); }}
              className="text-text-secondary hover:text-brand"
              data-testid="back-to-folders"
            >
              <ArrowLeft size={18} className="mr-1" /> Back
            </Button>
            <div>
              <div className="flex items-center gap-2">
                <FolderOpen size={24} className="text-brand" />
                <h2 className="text-2xl font-semibold text-text-primary">{selectedFolder.folder_name}</h2>
              </div>
              <p className="text-text-secondary text-sm mt-0.5">{folderProjects.length} project{folderProjects.length !== 1 ? 's' : ''}</p>
            </div>
          </div>
          {isAdmin && (
            <Dialog open={showDialog} onOpenChange={(open) => { setShowDialog(open); if (!open) resetCreateDialog(); }}>
              <DialogTrigger asChild>
                <Button className="bg-brand hover:bg-brand-hover text-white" data-testid="create-project-button">
                  <Plus size={18} className="mr-1.5" /> New Project
                </Button>
              </DialogTrigger>
              <DialogContent className={`bg-white ${dialogStep === 'template' ? 'max-w-2xl' : ''}`}>
                <DialogHeader>
                  <DialogTitle className="text-brand">
                    {dialogStep === 'template' ? 'Choose a Project Template' : 'Project Details'}
                  </DialogTitle>
                </DialogHeader>
                {dialogStep === 'template' ? (
                  <TemplateSelector
                    templates={templates}
                    onSelect={(tpl) => {
                      setSelectedTemplate(tpl);
                      setNewProject({ ...newProject, template_id: tpl?.id || '', drive_folder_id: selectedFolder.drive_folder_id });
                      setDialogStep('details');
                    }}
                  />
                ) : (
                  <ProjectForm
                    newProject={newProject}
                    setNewProject={setNewProject}
                    selectedTemplate={selectedTemplate}
                    users={users}
                    driveClients={driveClients}
                    mondayBoards={mondayBoards}
                    mondayConfigured={mondayConfigured}
                    lockedFolder={selectedFolder}
                    onSubmit={handleCreateProject}
                    onBack={() => { setDialogStep('template'); setSelectedTemplate(null); }}
                  />
                )}
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* Search */}
        {folderProjects.length > 3 && (
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-text-tertiary" size={18} />
            <Input placeholder="Search projects in this folder..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-11 glass" data-testid="folder-search-input" />
          </div>
        )}

        {/* Project Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="projects-grid">
          {filteredFolderProjects.map((project) => (
            <ProjectCard
              key={project.id}
              project={project}
              isAdmin={isAdmin}
              onMove={() => { setMoveProject(project); setMoveTargetFolder(''); }}
              onDelete={() => setDeleteProject(project)}
            />
          ))}
        </div>

        {filteredFolderProjects.length === 0 && (
          <EmptyState message={searchQuery ? 'No projects match your search' : 'No projects in this folder yet'} />
        )}

        {/* Move Dialog */}
        <MoveDialog
          project={moveProject}
          folders={driveClients}
          targetFolder={moveTargetFolder}
          setTargetFolder={setMoveTargetFolder}
          onMove={handleMoveProject}
          onClose={() => setMoveProject(null)}
        />

        {/* Delete Dialog */}
        <DeleteDialog project={deleteProject} onDelete={handleDeleteProject} onClose={() => setDeleteProject(null)} />
      </div>
    );
  }

  // ── FOLDERS GRID VIEW ──
  return (
    <div className="space-y-6" data-testid="projects-view">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-semibold tracking-tight text-text-primary mb-1">Projects</h2>
          <p className="text-text-secondary text-sm">{driveClients.length} client folder{driveClients.length !== 1 ? 's' : ''} from Google Drive</p>
        </div>
      </div>

      {/* Search folders */}
      {driveClients.length > 6 && (
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-text-tertiary" size={18} />
          <Input placeholder="Search client folders..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-11 glass" data-testid="folders-search-input" />
        </div>
      )}

      {/* Folder Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-testid="folders-grid">
        {filteredFolders.map((folder) => {
          const count = folderProjectCounts[folder.drive_folder_id] || 0;
          return (
            <button
              key={folder.drive_folder_id}
              onClick={() => { setSelectedFolder(folder); setSearchQuery(''); }}
              className="text-left group"
              data-testid={`folder-card-${folder.drive_folder_id}`}
            >
              <Card className="glass metric-card h-full hover:shadow-lg hover:border-brand/30 transition-all">
                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-brand/80 to-brand flex items-center justify-center">
                      <Folder className="text-white" size={22} />
                    </div>
                    <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-brand/10 text-brand">
                      {count} project{count !== 1 ? 's' : ''}
                    </span>
                  </div>
                  <h3 className="text-base font-bold text-text-primary mb-1 line-clamp-2 group-hover:text-brand transition-colors">
                    {folder.folder_name}
                  </h3>
                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-border-subtle">
                    <span className="text-xs text-text-tertiary">View projects</span>
                    <ChevronRight size={16} className="text-text-muted group-hover:text-brand transition-colors" />
                  </div>
                </CardContent>
              </Card>
            </button>
          );
        })}
      </div>

      {/* Unassigned projects notice */}
      {unassignedProjects.length > 0 && isAdmin && (
        <div className="border border-amber-200 bg-amber-50 rounded-xl p-4" data-testid="unassigned-notice">
          <h4 className="font-semibold text-amber-800 mb-2">{unassignedProjects.length} unassigned project{unassignedProjects.length !== 1 ? 's' : ''}</h4>
          <p className="text-sm text-amber-700 mb-3">These projects aren't linked to a Google Drive folder. Assign them to organize your workspace.</p>
          <div className="space-y-2">
            {unassignedProjects.map((p) => (
              <div key={p.id} className="flex items-center justify-between bg-white rounded-lg px-3 py-2 border border-amber-100">
                <span className="text-sm font-medium text-text-primary">{p.name}</span>
                <Button
                  size="sm" variant="outline"
                  onClick={() => { setMoveProject(p); setMoveTargetFolder(''); }}
                  className="text-xs h-7 border-brand/30 text-brand hover:bg-brand/10"
                  data-testid={`assign-project-${p.id}`}
                >
                  <MoveRight size={14} className="mr-1" /> Assign
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {driveClients.length === 0 && (
        <EmptyState message="No Google Drive client folders found. Check your Drive integration in Settings." />
      )}

      {/* Move Dialog (for unassigned) */}
      <MoveDialog
        project={moveProject}
        folders={driveClients}
        targetFolder={moveTargetFolder}
        setTargetFolder={setMoveTargetFolder}
        onMove={handleMoveProject}
        onClose={() => setMoveProject(null)}
      />
    </div>
  );
};

// ── Sub-components ──

function ProjectCard({ project, isAdmin, onMove, onDelete }) {
  return (
    <div className="relative group" data-testid={`project-card-${project.id}`}>
      <Link to={`/internal/projects/${project.id}`}>
        <Card className="glass metric-card h-full hover:shadow-lg transition-all">
          <CardContent className="p-5">
            <div className="flex items-start justify-between mb-3">
              <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-brand to-brand-hover flex items-center justify-center">
                <FileText className="text-white" size={20} />
              </div>
              <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${
                project.status === 'Active' ? 'bg-positive/20 text-positive' : 'bg-neutral/20 text-text-primary'
              }`}>{project.status}</span>
            </div>
            <h3 className="text-base font-bold text-text-primary mb-1 line-clamp-2">{project.name}</h3>
            {project.template_id && (
              <div className="flex items-center gap-1 mb-1"><Sparkles size={11} className="text-brand" /><span className="text-xs text-brand font-medium">Template</span></div>
            )}
            {project.monday_board_id && (
              <div className="flex items-center gap-1 mb-1"><LayoutGrid size={11} className="text-brand" /><span className="text-xs text-brand font-medium">Monday</span></div>
            )}
            <div className="flex items-center justify-between mt-3 pt-3 border-t border-border-subtle">
              <span className="text-xs text-text-secondary">View details</span>
              <ArrowRight size={16} className="text-brand" />
            </div>
          </CardContent>
        </Card>
      </Link>
      {isAdmin && (
        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
          <button onClick={(e) => { e.preventDefault(); onMove(); }} className="p-1.5 rounded-lg bg-white/90 shadow-sm hover:bg-brand/10 text-text-tertiary hover:text-brand" title="Move to folder" data-testid={`move-project-${project.id}`}>
            <MoveRight size={14} />
          </button>
          <button onClick={(e) => { e.preventDefault(); onDelete(); }} className="p-1.5 rounded-lg bg-white/90 shadow-sm hover:bg-red-50 text-text-tertiary hover:text-red-500" title="Delete" data-testid={`delete-project-${project.id}`}>
            <Trash2 size={14} />
          </button>
        </div>
      )}
    </div>
  );
}

function TemplateSelector({ templates, onSelect }) {
  return (
    <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1" data-testid="template-selection">
      <button type="button" onClick={() => onSelect(null)} className="w-full text-left p-4 rounded-xl border-2 border-dashed border-border-subtle hover:border-brand/40 hover:bg-brand/5 transition-all group" data-testid="template-blank">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-surface-page flex items-center justify-center"><Plus size={20} className="text-text-tertiary group-hover:text-brand transition-colors" /></div>
          <div><h4 className="font-semibold text-text-primary">Blank Project</h4><p className="text-sm text-text-tertiary">Start from scratch</p></div>
          <ChevronRight size={18} className="ml-auto text-text-muted group-hover:text-brand" />
        </div>
      </button>
      {templates.map((tpl) => (
        <button key={tpl.id} type="button" onClick={() => onSelect(tpl)} className="w-full text-left p-4 rounded-xl border border-border-subtle hover:border-brand/40 hover:bg-brand/5 transition-all group" data-testid={`template-card-${tpl.id}`}>
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-brand to-brand-hover flex items-center justify-center text-white text-lg flex-shrink-0">{CHANNEL_ICONS[tpl.channel] || <Sparkles size={18} />}</div>
            <div className="flex-1 min-w-0">
              <h4 className="font-semibold text-text-primary">{tpl.name}</h4>
              <p className="text-sm text-text-tertiary mt-0.5 line-clamp-1">{tpl.description}</p>
              <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-text-secondary">
                <span className="flex items-center gap-1"><FileText size={12} />{tpl.deliverables?.length || 0} deliverables</span>
                <span className="flex items-center gap-1"><Clock size={12} />{tpl.milestones?.length || 0} milestones</span>
                <span className="flex items-center gap-1"><Users size={12} />{tpl.resource_assignments?.length || 0} roles</span>
              </div>
            </div>
            <ChevronRight size={18} className="mt-1 text-text-muted group-hover:text-brand flex-shrink-0" />
          </div>
        </button>
      ))}
    </div>
  );
}

function ProjectForm({ newProject, setNewProject, selectedTemplate, users, driveClients, mondayBoards, mondayConfigured, lockedFolder, onSubmit, onBack }) {
  return (
    <div className="space-y-4">
      {selectedTemplate && (
        <div className="p-3 rounded-xl bg-brand/5 border border-brand/20">
          <div className="flex items-center gap-2">
            <Sparkles size={14} className="text-brand" />
            <span className="text-sm font-medium text-brand">Template: {selectedTemplate.name}</span>
          </div>
        </div>
      )}
      <form onSubmit={onSubmit} className="space-y-4" data-testid="create-project-form">
        <div>
          <Label>Project Name</Label>
          <Input placeholder="Summer Campaign 2025" value={newProject.name} onChange={(e) => setNewProject({ ...newProject, name: e.target.value })} required className="mt-1.5" data-testid="project-name-input" />
        </div>

        {/* Drive Folder — locked if inside a folder, selectable otherwise */}
        <div>
          <Label>Google Drive Folder</Label>
          {lockedFolder ? (
            <div className="mt-1.5 px-3 py-2 border border-brand/20 rounded-xl bg-brand/5 text-sm text-brand font-medium flex items-center gap-2">
              <Folder size={16} /> {lockedFolder.folder_name}
            </div>
          ) : (
            <select value={newProject.drive_folder_id} onChange={(e) => setNewProject({ ...newProject, drive_folder_id: e.target.value })} required className="w-full mt-1.5 px-3 py-2 border border-input rounded-xl bg-white focus:ring-2 focus:ring-brand" data-testid="drive-folder-select">
              <option value="">Select a Drive folder (required)</option>
              {driveClients.map(c => (<option key={c.drive_folder_id} value={c.drive_folder_id}>{c.folder_name}</option>))}
            </select>
          )}
        </div>

        {/* User Account */}
        <div>
          <Label>Link to User Account</Label>
          <select value={newProject.client_id} onChange={(e) => setNewProject({ ...newProject, client_id: e.target.value })} required className="w-full mt-1.5 px-3 py-2 border border-input rounded-xl bg-white focus:ring-2 focus:ring-brand" data-testid="client-select">
            <option value="">Select user...</option>
            {users.map(u => (<option key={u.id} value={u.id}>{u.name} ({u.email})</option>))}
          </select>
        </div>

        {/* Monday Board */}
        {mondayConfigured && (
          <div>
            <Label><span className="flex items-center gap-1.5"><LayoutGrid size={14} className="text-brand" />Monday.com Board</span></Label>
            <select value={newProject.monday_board_id} onChange={(e) => setNewProject({ ...newProject, monday_board_id: e.target.value })} className="w-full mt-1.5 px-3 py-2 border border-input rounded-xl bg-white focus:ring-2 focus:ring-brand" data-testid="monday-board-select">
              <option value="">No Monday board (optional)</option>
              {mondayBoards.map(b => (<option key={b.id} value={b.id}>{b.name} — {b.items_count} items</option>))}
            </select>
          </div>
        )}

        <div className="flex gap-2 pt-2">
          <Button type="button" variant="outline" onClick={onBack} className="flex-1">Back</Button>
          <Button type="submit" className="flex-1 bg-brand hover:bg-brand-hover text-white" data-testid="submit-project">Create Project</Button>
        </div>
      </form>
    </div>
  );
}

function MoveDialog({ project, folders, targetFolder, setTargetFolder, onMove, onClose }) {
  const availableFolders = useMemo(
    () => (project ? folders.filter(f => f.drive_folder_id !== project.drive_folder_id) : []),
    [folders, project],
  );
  if (!project) return null;
  return (
    <Dialog open={!!project} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="bg-white max-w-md">
        <DialogHeader>
          <DialogTitle className="text-brand flex items-center gap-2">
            <MoveRight size={18} />
            {project.drive_folder_id ? 'Move' : 'Assign'} Project
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-text-secondary">
            {project.drive_folder_id ? 'Move' : 'Assign'} <strong>"{project.name}"</strong> to a Google Drive folder:
          </p>
          <select
            value={targetFolder}
            onChange={(e) => setTargetFolder(e.target.value)}
            className="w-full px-3 py-2 border border-input rounded-xl bg-white focus:ring-2 focus:ring-brand"
            data-testid="move-folder-select"
          >
            <option value="">Select folder...</option>
            {availableFolders.map(f => (
              <option key={f.drive_folder_id} value={f.drive_folder_id}>{f.folder_name}</option>
            ))}
          </select>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
            <Button onClick={onMove} disabled={!targetFolder} className="flex-1 bg-brand hover:bg-brand-hover text-white" data-testid="confirm-move-button">
              {project.drive_folder_id ? 'Move' : 'Assign'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function DeleteDialog({ project, onDelete, onClose }) {
  if (!project) return null;
  return (
    <Dialog open={!!project} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="bg-white max-w-md">
        <DialogHeader>
          <DialogTitle className="text-red-600 flex items-center gap-2">
            <Trash2 size={18} />
            Delete Project
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-text-secondary">
            Are you sure you want to delete <strong>"{project.name}"</strong>? This action cannot be undone.
          </p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
            <Button onClick={onDelete} className="flex-1 bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-delete-button">Delete</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function EmptyState({ message }) {
  return (
    <div className="text-center py-12">
      <Folder className="mx-auto text-text-tertiary mb-4" size={48} />
      <p className="text-text-secondary">{message}</p>
    </div>
  );
}

export default ProjectsView;
